import Navigation from "@/components/Navigation";
import { Card } from "@/components/ui/card";
import { Helmet } from "react-helmet";
import SchemaMarkup from "@/components/SchemaMarkup";
import Breadcrumb from "@/components/Breadcrumb";
import { createArticleSchema } from "@/utils/schemaHelpers";
import HoroscopeContent from "@/components/HoroscopeContent";

const HoroscopoAcuario = () => {
  const articleSchema = createArticleSchema(
    "Acuario: Horóscopo Diario, Características y Compatibilidad con los 12 Signos",
    "Descubre el horóscopo diario, semanal y mensual de Acuario. Predicciones de amor, trabajo, salud y dinero para personas nacidas entre el 20 de enero y el 18 de febrero.",
    `${window.location.origin}/horoscopo-acuario`
  );

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Inicio",
        "item": window.location.origin
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Horóscopo Acuario",
        "item": `${window.location.origin}/horoscopo-acuario`
      }
    ]
  };

  const breadcrumbItems = [
    { name: "Inicio", url: "/" },
    { name: "Horóscopo", url: "/horoscopo" },
    { name: "Acuario" }
  ];

  return (
    <>
      <Helmet>
        <title>Acuario: Horóscopo Diario, Características y Compatibilidad con los 12 Signos</title>
        <meta name="description" content="Descubre el horóscopo diario, semanal y mensual de Acuario. Predicciones de amor, trabajo, salud y dinero para personas nacidas entre el 20 de enero y el 18 de febrero." />
        <link rel="canonical" href={`${window.location.origin}/horoscopo-acuario`} />
        <meta property="og:title" content="Acuario: Horóscopo Diario, Características y Compatibilidad" />
        <meta property="og:description" content="Descubre el horóscopo diario, semanal y mensual de Acuario. Predicciones de amor, trabajo, salud y dinero." />
        <meta property="og:url" content={`${window.location.origin}/horoscopo-acuario`} />
        <meta property="og:type" content="article" />
      </Helmet>
      <SchemaMarkup schema={articleSchema} />
      <SchemaMarkup schema={breadcrumbSchema} />
      
      <div className="min-h-screen">
        <Navigation />
        
        <main className="pt-24 pb-12 px-4">
          <div className="container mx-auto max-w-4xl">
            <Breadcrumb items={breadcrumbItems} />
            <div className="text-center mb-12">
              <div className="text-6xl mb-4">♒</div>
              <h1 className="text-2xl font-bold mb-4 text-glow">
                Horóscopo de Acuario
              </h1>
              <p className="text-xl text-muted-foreground">20 de Enero - 18 de Febrero</p>
            </div>

            <section className="mb-16">
              <h2 className="text-2xl font-bold mb-6 text-primary">Tu Horóscopo Acuario: Predicciones Actualizadas</h2>
              <HoroscopeContent sign="acuario" signName="Acuario" />
            </section>

            <section className="mb-16">
              <h2 className="text-2xl font-bold mb-6 text-secondary">Características Profundas del Signo Acuario: El Visionario Humanitario</h2>
              
              <Card className="p-8 mb-6 gradient-mystic">
                <h3 className="text-xl font-bold mb-4">Los Pilares Astrológicos de Acuario</h3>
                <div className="grid md:grid-cols-3 gap-6">
                  <div>
                    <h4 className="font-bold text-base mb-2 text-primary">💨 Elemento: Aire</h4>
                    <p className="text-muted-foreground">Enfoque en las ideas, la innovación y el colectivo. Acuario vive en el mundo del futuro.</p>
                  </div>
                  <div>
                    <h4 className="font-bold text-base mb-2 text-secondary">⚡ Modalidad: Fijo</h4>
                    <p className="text-muted-foreground">Define la determinación en sus ideales y la firmeza en sus convicciones revolucionarias.</p>
                  </div>
                  <div>
                    <h4 className="font-bold text-base mb-2 text-accent">♅♄ Planetas Regentes</h4>
                    <p className="text-muted-foreground">Urano (Revolución) y Saturno (Estructura). Innovación con propósito y disciplina.</p>
                  </div>
                </div>
              </Card>

              <Card className="p-8 mb-6 gradient-celestial">
                <h3 className="text-xl font-bold mb-4">Perfil de Personalidad Acuario: Fortalezas y Debilidades</h3>
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <h4 className="font-bold text-base mb-3 text-primary">✨ Fortalezas</h4>
                    <ul className="space-y-2 text-muted-foreground">
                      <li>• <strong>Originalidad:</strong> Pensamiento único e innovador</li>
                      <li>• <strong>Humanitarismo:</strong> Preocupación genuina por el colectivo</li>
                      <li>• <strong>Independencia:</strong> Libertad mental absoluta</li>
                      <li>• <strong>Visión:</strong> Capacidad de ver el futuro</li>
                      <li>• <strong>Objetividad:</strong> Perspectiva imparcial</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-bold text-base mb-3 text-secondary">⚠️ Debilidades</h4>
                    <ul className="space-y-2 text-muted-foreground">
                      <li>• <strong>Frialdad Emocional:</strong> Distancia afectiva</li>
                      <li>• <strong>Excentricidad:</strong> Puede alienar a otros</li>
                      <li>• <strong>Terquedad Ideológica:</strong> Inflexible en ideas</li>
                      <li>• <strong>Desapego:</strong> Dificultad con intimidad</li>
                      <li>• <strong>Imprevisibilidad:</strong> Cambios bruscos</li>
                    </ul>
                  </div>
                </div>
                <div className="text-center p-4 bg-primary/10 rounded-lg">
                  <p className="text-lg font-bold text-primary">Mantra Clave: "Yo Sé"</p>
                </div>
              </Card>

              <Card className="p-8 mb-6 gradient-mystic">
                <h3 className="text-xl font-bold mb-4">Acuario en el Amor: Amistad, Libertad e Intelectualidad</h3>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Para Acuario, el amor comienza con la amistad y la conexión mental. Busca una pareja que respete su independencia y comparta sus ideales. El romance tradicional puede aburrirle; prefiere relaciones únicas que rompan con las convenciones.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Necesita espacio para explorar ideas y causas. Puede parecer emocionalmente distante, pero su amor es leal y duradero una vez comprometido. Busca una pareja que sea su igual intelectual y su mejor amigo. La libertad es innegociable.
                </p>
              </Card>

              <Card className="p-8 gradient-celestial">
                <h3 className="text-xl font-bold mb-4">Acuario en el Trabajo y Finanzas: Tecnología, Innovación y Causas Sociales</h3>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Acuario destaca en tecnología, ciencias, investigación, causas sociales, organizaciones humanitarias y cualquier campo que requiera pensamiento innovador. Su visión de futuro lo hace ideal para startups y proyectos revolucionarios.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Con las finanzas, Acuario tiene una relación peculiar. Puede ser generoso con causas pero desapegado de posesiones materiales. Le atraen inversiones tecnológicas y empresas con impacto social. Excelente para crear soluciones financieras innovadoras.
                </p>
              </Card>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-6 text-accent">Compatibilidad de Acuario con los Otros Signos Zodiacales</h2>
              
              <Card className="p-8 mb-6 gradient-mystic">
                <h3 className="text-xl font-bold mb-4">💚 Tu Match Perfecto</h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-bold text-base mb-2">♊ Géminis y ♎ Libra</h4>
                    <p className="text-muted-foreground">
                      Los signos de aire comparten amor por las ideas y la comunicación. Con Géminis hay estimulación mental constante. Con Libra, una búsqueda compartida de justicia y armonía social.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-bold text-base mb-2">♈ Aries y ♐ Sagitario</h4>
                    <p className="text-muted-foreground">
                      Los signos de fuego aportan la pasión que complementa la visión de Acuario. Aries trae acción e iniciativa, mientras que Sagitario añade filosofía y aventura compartida.
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-8 gradient-celestial">
                <h3 className="text-xl font-bold mb-4">⚡ Los Desafíos y Lecciones de Acuario</h3>
                <p className="text-muted-foreground mb-4">
                  La intimidad emocional es el gran desafío de Acuario. Leo, su signo opuesto, le enseña sobre expresión personal, reconocimiento de necesidades individuales y el valor del ego sano.
                </p>
                <p className="text-muted-foreground">
                  Los signos de agua (Cáncer, Escorpio, Piscis) desafían su distancia emocional con profundidad afectiva. Los signos de tierra (Tauro, Virgo, Capricornio) le enseñan sobre la importancia de lo práctico y tangible.
                </p>
              </Card>
            </section>
          </div>
        </main>
      </div>
    </>
  );
};

export default HoroscopoAcuario;
